/**
 * Tela /admin/arts/review — fila de curadoria de artes (TH-55167).
 *
 * Consome GET /custom-arts/pending (TH-55163) e expõe ações Aprovar/Rejeitar.
 *
 * Acesso:
 *   - super_admin: vê todos os pending (scope=global + supplier).
 *   - catalog_operator: backend força scope='supplier' (filtro UI fica
 *     desabilitado para esse role).
 *   - Outras roles: 403 visual.
 *
 * Aprovar: POST /custom-arts/:id/approve  (notes opcional).
 * Rejeitar: POST /custom-arts/:id/reject  (notes OBRIGATÓRIO — modal força).
 *
 * UX: grid de cards (thumb 16:9 + nome + supplier + data submissão), filtros
 * por scope, paginação. Após approve/reject a query é invalidada e o card
 * some da fila.
 *
 * PSHWO-64518: cada card mostra "Vistas / Usos" (view_count / use_count)
 * lendo GET /custom-arts/:id/stats — é a leitura da telemetria de impressão
 * que o backend já capturava mas ninguém exibia.
 */

import { useMemo, useState } from "react";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { FilterPills } from "@/components/ui/FilterPills";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";
import {
  useApproveArt,
  usePendingArts,
  useRejectArt,
  type ArtReviewScopeFilter,
} from "@/hooks/useArtReview";
import { useArtStats } from "@/hooks/useArtStats";
import type { CustomArtRead } from "@/hooks/useCreateCustomArt";
import { cn } from "@/lib/utils";

const PER_PAGE = 20;

const SCOPE_OPTIONS: { label: string; value: ArtReviewScopeFilter }[] = [
  { label: "Todas", value: "all" },
  { label: "Priceshow (global)", value: "global" },
  { label: "Fornecedor", value: "supplier" },
];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ArtsReviewPage() {
  return (
    <RequireAuth>
      <ArtsReviewContent />
    </RequireAuth>
  );
}

function ArtsReviewContent() {
  const { user, isLoading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <AppLayout
        breadcrumb="OPERAÇÃO / CURADORIA DE ARTES"
        title="Curadoria de artes"
      >
        <div className="text-sm text-[var(--color-muted-foreground)]">
          carregando…
        </div>
      </AppLayout>
    );
  }

  if (user?.role !== "super_admin" && user?.role !== "catalog_operator") {
    return (
      <AppLayout
        breadcrumb="OPERAÇÃO / CURADORIA DE ARTES"
        title="Curadoria de artes"
      >
        <NotAuthorized />
      </AppLayout>
    );
  }

  return <ArtsReviewDashboard isCatalogOperator={user.role === "catalog_operator"} />;
}

interface ArtsReviewDashboardProps {
  isCatalogOperator: boolean;
}

function ArtsReviewDashboard({ isCatalogOperator }: ArtsReviewDashboardProps) {
  // catalog_operator: backend força scope='supplier' independentemente do filtro.
  // Mantemos o filtro travado para deixar a UX honesta.
  const [scope, setScope] = useState<ArtReviewScopeFilter>(
    isCatalogOperator ? "supplier" : "all",
  );
  const [page, setPage] = useState(1);

  const filters = useMemo(
    () => ({ scope, page, per_page: PER_PAGE }),
    [scope, page],
  );
  const query = usePendingArts(filters);
  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;

  // -- Modal de rejeição ----------------------------------------------------
  const [rejectTarget, setRejectTarget] = useState<CustomArtRead | null>(null);

  const actions = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => void query.refetch()}
      disabled={query.isFetching}
      aria-label="Atualizar"
    >
      <RefreshCw
        className={cn(
          "mr-2 h-4 w-4",
          query.isFetching ? "animate-spin" : null,
        )}
      />
      {query.isFetching ? "Atualizando…" : "Atualizar"}
    </Button>
  );

  return (
    <AppLayout
      breadcrumb="OPERAÇÃO / CURADORIA DE ARTES"
      title="Curadoria de artes"
      actions={actions}
    >
      <div className="mx-auto max-w-7xl space-y-4">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Artes submetidas por fornecedores e equipe Priceshow, aguardando
          aprovação.{" "}
          {total > 0 ? (
            <span className="ml-1 font-mono tabular-nums text-[var(--color-foreground)]">
              {total} {total === 1 ? "pendente" : "pendentes"}
            </span>
          ) : null}
        </p>

        {/* Filtro de scope — desabilitado para catalog_operator (backend força). */}
        {!isCatalogOperator ? (
          <div role="tablist" aria-label="Filtrar por origem">
            <FilterPills
              options={SCOPE_OPTIONS}
              value={scope}
              onChange={(v) => {
                setScope(v);
                setPage(1);
              }}
            />
          </div>
        ) : (
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Você só pode revisar artes de fornecedor.
          </p>
        )}

        {query.error ? (
          <div
            role="alert"
            className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
          >
            Erro ao carregar fila.{" "}
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="underline"
            >
              Tentar novamente
            </button>
          </div>
        ) : null}

        {query.isLoading ? (
          <QueueSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState />
        ) : (
          <ul
            role="list"
            aria-label="Fila de curadoria"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {rows.map((art) => (
              <li key={art.id}>
                <ReviewCard
                  art={art}
                  onReject={() => setRejectTarget(art)}
                />
              </li>
            ))}
          </ul>
        )}

        {query.data && total > PER_PAGE ? (
          <div className="mt-4">
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={total}
              onPageChange={setPage}
            />
          </div>
        ) : null}
      </div>

      <RejectArtDialog
        art={rejectTarget}
        open={rejectTarget !== null}
        onOpenChange={(o) => {
          if (!o) setRejectTarget(null);
        }}
      />
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Card de revisão
// ---------------------------------------------------------------------------

interface ReviewCardProps {
  art: CustomArtRead;
  onReject: () => void;
}

function ReviewCard({ art, onReject }: ReviewCardProps) {
  const approve = useApproveArt();

  const originLabel =
    art.scope === "global"
      ? "Priceshow"
      : art.scope === "supplier"
        ? art.supplier_name ?? "Fornecedor"
        : "Tenant";

  const originTone =
    art.scope === "global" ? "success" : art.scope === "supplier" ? "warn" : "muted";

  return (
    <article className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]">
      <div className="relative aspect-video w-full bg-[var(--color-muted-tint)]">
        <img
          src={art.art_url}
          alt={`Arte: ${art.name}`}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3
            className="line-clamp-2 text-sm font-medium leading-tight"
            title={art.name}
          >
            {art.name}
          </h3>
          <StatusPill tone={originTone}>{originLabel}</StatusPill>
        </div>
        <p className="font-mono text-[11px] text-[var(--color-muted-foreground)]">
          Submetida em {formatDate(art.created_at)}
        </p>
        <p className="text-[11px] text-[var(--color-muted-foreground)]">
          {art.width}×{art.height} • {(art.file_size_bytes / 1024).toFixed(0)} KB
        </p>
        <ArtCounters artId={art.id} />
        <div className="flex gap-2 pt-2">
          <Button
            size="sm"
            onClick={() =>
              approve.mutate(
                { id: art.id },
                {
                  onError: (err) => {
                    // Espalha via alert simples; opções de toast viriam de
                    // um sistema global ainda não montado.

                    alert(`Falha ao aprovar: ${err.message}`);
                  },
                },
              )
            }
            disabled={approve.isPending}
            className="flex-1"
            aria-label={`Aprovar arte ${art.name}`}
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {approve.isPending ? "Aprovando…" : "Aprovar"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            className="flex-1"
            aria-label={`Rejeitar arte ${art.name}`}
          >
            <XCircle className="h-4 w-4" aria-hidden="true" />
            Rejeitar
          </Button>
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Coluna "Vistas / Usos" (PSHWO-64518)
// ---------------------------------------------------------------------------

/**
 * Contadores de impressão (view_count) e clone (use_count) da arte.
 *
 * Lê GET /custom-arts/{id}/stats. Falha de leitura vira "—": a métrica é
 * acessória à curadoria e não pode derrubar o card nem esconder os botões.
 */
function ArtCounters({ artId }: { artId: string }) {
  const stats = useArtStats(artId);
  const data = stats.data;

  const value = data
    ? `${data.view_count} / ${data.use_count}`
    : stats.isLoading
      ? "…"
      : "—";

  const title = data
    ? `${data.view_count} visualizações (${data.unique_tenants_viewed} tenants, ` +
      `${data.last_7_days_views} nos últimos 7 dias) • ` +
      `${data.use_count} usos (${data.last_7_days_uses} nos últimos 7 dias)`
    : "Telemetria indisponível";

  return (
    <p
      className="text-[11px] text-[var(--color-muted-foreground)]"
      title={title}
    >
      <span className="uppercase tracking-[0.08em]">Vistas / Usos</span>{" "}
      <span
        className="font-mono tabular-nums text-[var(--color-foreground)]"
        aria-label={`Vistas e usos da arte: ${value}`}
      >
        {value}
      </span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Modal de rejeição (notes obrigatório)
// ---------------------------------------------------------------------------

interface RejectArtDialogProps {
  art: CustomArtRead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function RejectArtDialog({ art, open, onOpenChange }: RejectArtDialogProps) {
  const [notes, setNotes] = useState("");
  const reject = useRejectArt();

  // Reset notes ao fechar.
  function handleOpenChange(next: boolean) {
    if (!next) setNotes("");
    onOpenChange(next);
  }

  const trimmed = notes.trim();
  const canSubmit = trimmed.length > 0 && !reject.isPending;

  function handleSubmit() {
    if (!art || !trimmed) return;
    reject.mutate(
      { id: art.id, notes: trimmed },
      {
        onSuccess: () => {
          setNotes("");
          onOpenChange(false);
        },
        onError: (err) => {

          alert(`Falha ao rejeitar: ${err.message}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby="reject-art-desc">
        <DialogTitle>Rejeitar arte</DialogTitle>
        <DialogDescription id="reject-art-desc">
          O motivo é registrado em audit log e fica visível no histórico
          da arte.
        </DialogDescription>

        <div className="mt-4 space-y-3">
          {art ? (
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-muted-tint)] px-3 py-2 text-xs">
              <div className="font-medium text-[var(--color-foreground)]">
                {art.name}
              </div>
              <div className="mt-0.5 font-mono text-[10px] text-[var(--color-muted-foreground)]">
                {art.id.slice(0, 8)}
              </div>
            </div>
          ) : null}

          <label
            htmlFor="reject-notes"
            className="block text-xs uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]"
          >
            Motivo da rejeição *
          </label>
          <Input
            id="reject-notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex.: logo cortado, preço ilegível…"
            aria-required="true"
            aria-invalid={!trimmed}
          />
          {!trimmed ? (
            <p className="text-[11px] text-[var(--color-muted-foreground)]">
              Informe um motivo para liberar o botão.
            </p>
          ) : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={reject.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-label="Confirmar rejeição"
          >
            {reject.isPending ? "Rejeitando…" : "Rejeitar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Skeleton + empty state + 403
// ---------------------------------------------------------------------------

function QueueSkeleton() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      aria-busy="true"
      aria-label="Carregando fila…"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]"
        >
          <Skeleton className="aspect-video w-full rounded-none" />
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="mt-2 h-8 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-md border border-dashed border-[var(--color-border-strong)] p-10 text-center">
      <CheckCircle2
        className="mx-auto h-10 w-10 text-[var(--color-success)]"
        aria-hidden="true"
      />
      <h3 className="mt-3 text-base font-medium">Fila zerada.</h3>
      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
        Nenhuma arte aguarda revisão no momento.
      </p>
    </div>
  );
}

function NotAuthorized() {
  return (
    <div className="mx-auto max-w-xl rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-8 text-center">
      <div className="text-base font-semibold">Acesso restrito</div>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
        Esta página é exclusiva para super_admin e catalog_operator. Se você
        precisa de acesso, fale com o time responsável pela operação.
      </p>
    </div>
  );
}
